"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { APP_AVAILABLE } from "@/lib/app-availability";

/**
 * Client component that tries to open the native app via custom
 * scheme on tap, falling back to the Play Store / App Store after
 * a short timeout if the app isn't installed.
 *
 * Android uses an `intent://` URL per the Chrome intent spec — this
 * opens the app if installed, otherwise Chrome navigates to the
 * S.browser_fallback_url. iOS uses the `ishvaram://` custom scheme
 * directly; if the app isn't installed the location just fails
 * silently and our JS timer falls back to the App Store.
 *
 * `mobilePath` must NOT include the leading `ishvaram://` — pass
 * just the route, e.g. `chat/abc123?persona=vinod`. The component
 * builds the full URL for each platform.
 */
export default function AppDeepLink({
  mobilePath,
  children,
  className,
  style,
  storeFallback = "https://play.google.com/store/apps/details?id=com.ishvaram.app",
  webFallbackPath,
}: {
  mobilePath: string;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  storeFallback?: string;
  /**
   * On-site destination used while APP_AVAILABLE is false. Without it the
   * children render as an inert span — which is a rule-32 dead click when the
   * children look tappable (the /hi/astrologers persona cards, 2026-08-13).
   */
  webFallbackPath?: string;
}) {
  const anchorRef = useRef<HTMLAnchorElement>(null);
  // Default href for SSR + bots: Play Store. Overridden client-side
  // by the handler below once we can sniff user-agent.
  const defaultHref = storeFallback;

  useEffect(() => {
    // No-op — SSR-safe.
  }, []);

  function handleClick(ev: React.MouseEvent<HTMLAnchorElement>) {
    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    if (!isAndroid && !isIOS) {
      // Desktop / other: keep the Play Store fallback behaviour.
      return;
    }
    ev.preventDefault();
    const fallback = encodeURIComponent(storeFallback);

    if (isAndroid) {
      // Chrome intent URL — opens app if installed, else falls back.
      window.location.href = `intent://${mobilePath}#Intent;scheme=ishvaram;package=com.ishvaram.app;S.browser_fallback_url=${fallback};end`;
      return;
    }

    // iOS: try custom scheme, fall back to App Store after 1.5s if
    // the page stays focused (meaning the app didn't open).
    const start = Date.now();
    window.location.href = `ishvaram://${mobilePath}`;
    setTimeout(() => {
      if (!document.hidden && Date.now() - start < 2500) {
        window.location.href = storeFallback;
      }
    }, 1500);
  }

  // No public app yet → don't wrap content in an app/store deep link. With a
  // web fallback the surface stays genuinely tappable; without one, render the
  // children plainly so the surface stays intact without a broken install path.
  if (!APP_AVAILABLE) {
    if (webFallbackPath) {
      return (
        <a href={webFallbackPath} className={className} style={style}>
          {children}
        </a>
      );
    }
    return (
      <span className={className} style={style}>
        {children}
      </span>
    );
  }

  return (
    <a
      ref={anchorRef}
      href={defaultHref}
      onClick={handleClick}
      className={className}
      style={style}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}
